import { useCallback, useEffect, useRef } from "react";

const DEFAULT_BURST_GAP_THRESHOLD_MS = 100;

interface UseDebouncedCallbackOptions {
  burstGapThresholdMs?: number;
}

export function useBurstDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delay: number,
  {
    burstGapThresholdMs = DEFAULT_BURST_GAP_THRESHOLD_MS,
  }: UseDebouncedCallbackOptions = {}
) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const burstStartTimeRef = useRef(0);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  const debouncedCallback = useCallback(
    (...args: Args) => {
      const now = Date.now();

      if (timeoutRef.current) {
        const isNewBurst =
          now - burstStartTimeRef.current > burstGapThresholdMs;
        if (isNewBurst) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }

      burstStartTimeRef.current = now;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [burstGapThresholdMs, delay]
  );

  return { debouncedCallback, cancel };
}
